<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <section class="relative bg-gray-900 text-white overflow-hidden">
      <div class="absolute inset-0 bg-black opacity-20"></div>
      <div class="absolute inset-0">
        <div
          class="absolute top-10 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"
        ></div>
        <div
          class="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-2000"
        ></div>
        <div
          class="absolute bottom-10 left-1/2 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-4000"
        ></div>
      </div>

      <div class="relative z-10 container mx-auto px-6 py-20 text-center">
        <h1
          class="text-4xl md:text-6xl mt-16 font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent"
        >
          Change Lives Today
        </h1>
        <p
          class="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed opacity-90"
        >
          Every donation creates ripples of hope. Join thousands of
          compassionate individuals making a real difference in communities
          worldwide.
        </p>
        <div
          class="flex flex-col sm:flex-row justify-center items-center gap-6 mb-12"
        >
          <div class="flex items-center text-lg">
            <div
              class="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"
            ></div>
            <span>2,847 lives impacted this month</span>
          </div>
          <div class="flex items-center text-lg">
            <div
              class="w-3 h-3 bg-yellow rounded-full mr-3 animate-pulse"
            ></div>
            <span>$127,000 raised this year</span>
          </div>
        </div>
        <button
          @click="scrollToForm"
          class="bg-yellow text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
        >
          Start Making a Difference
        </button>
      </div>
    </section>
    <div class="absolute inset-0 bg-black bg-opacity-75"></div>

    <!-- Impact Statistics -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          <div
            class="group hover:transform hover:scale-105 transition-all duration-300"
          >
            <div class="text-4xl font-bold text-blue-600 mb-2">15,000+</div>
            <div class="text-gray-600">People Helped</div>
          </div>
          <div
            class="group hover:transform hover:scale-105 transition-all duration-300"
          >
            <div class="text-4xl font-bold text-green-600 mb-2">98%</div>
            <div class="text-gray-600">Direct Impact</div>
          </div>
          <div
            class="group hover:transform hover:scale-105 transition-all duration-300"
          >
            <div class="text-4xl font-bold text-purple-600 mb-2">50+</div>
            <div class="text-gray-600">Countries Reached</div>
          </div>
          <div
            class="group hover:transform hover:scale-105 transition-all duration-300"
          >
            <div class="text-4xl font-bold text-orange-600 mb-2">24/7</div>
            <div class="text-gray-600">Support Available</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <div class="container mx-auto px-6 py-16">
      <div class="flex flex-col lg:flex-row gap-12 items-start">
        <!-- Left Section - Enhanced -->
        <div
          class="w-full lg:w-1/2 h-f bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
        >
          <div class="text-center mb-8">
            <h2 class="text-3xl font-bold text-gray-800 mb-4">
              Why Your Donation Matters
            </h2>
            <div
              class="w-20 h-1 bg-gradient-to-r from-blue-500 to-main_blue mx-auto rounded-full"
            ></div>
          </div>

          <div class="relative mb-8 overflow-hidden rounded-xl">
            <img
              src="../assets/images/donate.jpg"
              alt="People helping community"
              class="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-500"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"
            ></div>
            <div class="absolute bottom-4 left-4 text-white">
              <p class="text-sm font-medium">Real impact in action</p>
            </div>
          </div>

          <p class="text-gray-600 mb-8 text-lg leading-relaxed">
            Your generosity directly funds life-changing programs that provide
            essential services, emergency relief, and sustainable solutions for
            communities worldwide. Every dollar creates lasting positive change.
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              class="flex items-center space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100 hover:shadow-md transition-shadow"
            >
              <div
                class="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"
              >
                <span class="text-white text-sm">✓</span>
              </div>
              <span class="text-gray-700 font-medium">One-time Impact</span>
            </div>
            <div
              class="flex items-center space-x-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100 hover:shadow-md transition-shadow"
            >
              <div
                class="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center"
              >
                <span class="text-white text-sm">✓</span>
              </div>
              <span class="text-gray-700 font-medium">Monthly Partnership</span>
            </div>
            <div
              class="flex items-center space-x-3 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-100 hover:shadow-md transition-shadow"
            >
              <div
                class="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center"
              >
                <span class="text-white text-sm">✓</span>
              </div>
              <span class="text-gray-700 font-medium">Annual Commitment</span>
            </div>
            <div
              class="flex items-center space-x-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100 hover:shadow-md transition-shadow"
            >
              <div
                class="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center"
              >
                <span class="text-white text-sm">✓</span>
              </div>
              <span class="text-gray-700 font-medium">Ongoing Support</span>
            </div>
          </div>
        </div>

        <!-- Right Section (Form) - Enhanced -->
        <div
          ref="donationForm"
          class="w-full lg:w-1/2 h-full bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
        >
          <div class="text-center mb-8 ">
            <h2 class="text-3xl font-bold text-gray-800 mb-10">
              Become a Hero Today
            </h2>
            <p class="text-gray-600">
              Fill out the form below to make your donation
            </p>
            <div
              class="w-20 h-1 bg-gradient-to-r from-orange-500 to-yellow mx-auto rounded-full mt-4"
            ></div>
          </div>

          <form @submit.prevent="submitForm" class="space-y-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="relative">
                <input
                  type="text"
                  placeholder="First name *"
                  class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white"
                  v-model="form.firstName"
                  required
                />
              </div>
              <div class="relative">
                <input
                  type="text"
                  placeholder="Last name"
                  class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white"
                  v-model="form.lastName"
                />
              </div>
            </div>

            <div class="relative">
              <input
                type="email"
                placeholder="Email Address *"
                class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white"
                v-model="form.email"
                required
              />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="relative">
                <input
                  type="text"
                  placeholder="Country"
                  class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white"
                  v-model="form.country"
                />
              </div>
              <div
                class="flex gap-0 border-2 border-gray-200 overflow-hidden rounded-lg bg-gray-50 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-300"
              >
                <select
                  class="w-1/3 px-3 py-3 focus:outline-none focus:border-none focus:ring-0 bg-gray-50 rounded-l-lg"
                  v-model="form.countryCode"
                >
                  <option value="+44">🇬🇧 UK</option>
                  <option value="+1">🇺🇸 US</option>
                  <option value="+234">🇳🇬 NG</option>
                </select>
                <input
                  type="tel"
                  placeholder="Phonenumber"
                  class="w-2/3 px-4 py-3 focus:outline-none focus:border-none focus:ring-0 bg-gray-50 rounded-r-lg"
                  v-model="form.phone"
                />
              </div>
            </div>

            <div class="relative">
              <select
                class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white appearance-none"
                v-model="form.amount"
                required
              >
                <option value="">Select donation amount</option>
                <option value="$25">$25 - Provides basic supplies</option>
                <option value="$50">$50 - Feeds a family for a week</option>
                <option value="$100">$100 - Supports education programs</option>
                <option value="$200">$200 - Emergency relief package</option>
                <option value="custom">Custom amount</option>
              </select>
            </div>

            <div v-if="form.amount === 'custom'" class="relative">
              <input
                type="number"
                placeholder="Enter custom amount"
                min="1"
                class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white"
                v-model="form.customAmount"
              />
            </div>

            <div class="relative">
              <select
                class="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-gray-50 focus:bg-white appearance-none"
                v-model="form.paymentMethod"
                required
              >
                <option disabled selected value="">
                  Select payment method
                </option>
                <option value="card">💳 Credit/Debit Card</option>
                <option value="paypal">🟦 Paystack</option>
                <option value="bank">🏦 Bank Transfer</option>
              </select>
            </div>

            <div class="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="anonymous"
                v-model="form.anonymous"
                class="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label
                for="anonymous"
                class="text-gray-700 font-medium cursor-pointer"
              >
                Keep my donation anonymous
              </label>
            </div>

            <button
              type="submit"
              class="w-full bg-yellow text-white py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">Complete Your Donation</span>
              <span v-else>Processing...</span>
            </button>
          </form>

          <div class="mt-6 text-center">
            <p class="text-sm text-gray-500">
              🔒 Your donation is secure and encrypted. We respect your privacy.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Trust Indicators -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-6 text-center">
        <h3 class="text-2xl font-bold text-gray-800 mb-8">
          Trusted by thousands worldwide
        </h3>
        <div
          class="flex flex-wrap justify-center items-center gap-8 opacity-60"
        >
          <div class="text-lg font-semibold">🌟 4.9/5 Rating</div>
          <div class="text-lg font-semibold">🔒 SSL Secured</div>
          <div class="text-lg font-semibold">✅ Verified Charity</div>
          <div class="text-lg font-semibold">🏆 Award Winner</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "DonationPage",
  data() {
    return {
      isSubmitting: false,
      form: {
        firstName: "",
        lastName: "",
        email: "",
        country: "",
        countryCode: "+234",
        phone: "",
        amount: "",
        customAmount: "",
        paymentMethod: "",
        anonymous: false,
      },
    };
  },
  methods: {
    scrollToForm() {
      this.$refs.donationForm.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    },
    async submitForm() {
      this.isSubmitting = true;

      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 2000));

        console.log("Form submitted:", {
          ...this.form,
          finalAmount:
            this.form.amount === "custom"
              ? this.form.customAmount
              : this.form.amount,
        });

        // Show success message or redirect
        alert(
          "Thank you for your donation! You will receive a confirmation email shortly."
        );

        // Reset form
        this.form = {
          firstName: "",
          lastName: "",
          email: "",
          country: "",
          countryCode: "+234",
          phone: "",
          amount: "",
          customAmount: "",
          paymentMethod: "",
          anonymous: false,
        };
      } catch (error) {
        console.error("Donation failed:", error);
        alert("There was an error processing your donation. Please try again.");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
@keyframes pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.5;
  }
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

/* Custom scrollbar for better UX */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
